import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import config from '../config/api'

// Deposit Modal Component
const DepositModal = ({ isOpen, onClose, onSuccess, wallet }) => {
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPaymentInfo, setShowPaymentInfo] = useState(false)
  const [transactionResult, setTransactionResult] = useState(null)
  const token = localStorage.getItem('token')

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      // Show temporary success message
      const button = document.getElementById(`copy-${type}`)
      const originalText = button.innerHTML
      button.innerHTML = '✅ Đã copy!'
      button.classList.add('bg-green-500')
      setTimeout(() => {
        button.innerHTML = originalText
        button.classList.remove('bg-green-500')
      }, 2000)
    }).catch(err => {
      console.error('Failed to copy: ', err)
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!amount || amount <= 0) {
      setError('Vui lòng nhập số tiền hợp lệ (lớn hơn 0)')
      setLoading(false)
      return
    }

    const transactionAmount = parseFloat(amount)
    if (isNaN(transactionAmount)) {
      setError('Số tiền không hợp lệ')
      setLoading(false)
      return
    }

    try {
      const res = await fetch(config.WALLET_API_URL + '/create-transaction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          amount: transactionAmount, 
          type: 'DEPOSIT', 
          description: description || '' 
        })
      })
      
      if (!res.ok) {
        let errorMessage = 'Tạo lệnh nạp tiền thất bại'
        try {
          const errorData = await res.json()
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail
          } else if (errorData.message) {
            errorMessage = errorData.message
          }
        } catch (parseError) {
          errorMessage = `Lỗi HTTP ${res.status}: ${res.statusText}`
        }
        throw new Error(errorMessage)
      }
      
      const data = await res.json()
      console.log('Transaction result:', data) // Debug log
      // Store the amount from form into the result object
      const resultWithAmount = { ...data, amount: transactionAmount }
      setTransactionResult(resultWithAmount)
      setShowPaymentInfo(true)
      onSuccess(resultWithAmount)
    } catch (err) {
      setError(err.message || 'Có lỗi xảy ra khi tạo lệnh nạp tiền')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setAmount('')
    setDescription('')
    setShowPaymentInfo(false)
    setTransactionResult(null)
    setError('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-green-700">💰 Tạo lệnh nạp tiền</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!showPaymentInfo ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Số tiền nạp *</label>
              <input 
                type="number" 
                min="1" 
                step="any" 
                value={amount} 
                onChange={e => setAmount(e.target.value)} 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" 
                placeholder="Nhập số tiền cần nạp"
                required 
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mô tả (tùy chọn)</label>
              <input 
                type="text" 
                value={description} 
                onChange={e => setDescription(e.target.value)} 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500" 
                placeholder="Ghi chú cho giao dịch"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <div className="text-red-600 text-sm">❌ {error}</div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Đang tạo...' : 'Tạo lệnh nạp tiền'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            {/* Success Message */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center mb-2">
                <span className="text-green-600 text-xl mr-2">✅</span>
                <span className="font-semibold text-green-800">Lệnh nạp tiền đã được tạo thành công!</span>
              </div>
              <p className="text-sm text-green-700">
                Vui lòng chuyển khoản theo thông tin bên dưới và đợi admin xác nhận.
              </p>
            </div>

            {/* Payment Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                <span className="mr-2">🏦</span>
                Thông tin chuyển khoản
              </h4>
              
              <div className="space-y-3">
                {/* Transaction Code */}
                <div className="flex justify-between items-center p-3 bg-white rounded border">
                  <div className="flex-1">
                    <div className="text-sm text-gray-600">Mã giao dịch</div>
                    <div className="font-mono font-semibold text-lg">
                      {transactionResult?.transaction_code || transactionResult?.transaction_id || transactionResult?.id || 'Đang tạo...'}
                    </div>
                  </div>
                  <button
                    id="copy-code"
                    onClick={() => copyToClipboard(
                      transactionResult?.transaction_code || 
                      transactionResult?.transaction_id || 
                      transactionResult?.id || 
                      'Đang tạo...', 
                      'code'
                    )}
                    className="ml-3 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    📋 Copy
                  </button>
                </div>

                {/* Bank Account Number */}
                <div className="flex justify-between items-center p-3 bg-white rounded border">
                  <div className="flex-1">
                    <div className="text-sm text-gray-600">Số tài khoản</div>
                    <div className="font-mono font-semibold text-lg">0071000900779</div>
                  </div>
                  <button
                    id="copy-account"
                    onClick={() => copyToClipboard('0071000900779', 'account')}
                    className="ml-3 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    📋 Copy
                  </button>
                </div>

                {/* Bank Name */}
                <div className="flex justify-between items-center p-3 bg-white rounded border">
                  <div className="flex-1">
                    <div className="text-sm text-gray-600">Ngân hàng</div>
                    <div className="font-semibold">Ngân hàng Ngoại thương Việt Nam (Vietcombank)</div>
                  </div>
                  <button
                    id="copy-bank"
                    onClick={() => copyToClipboard('Ngân hàng Ngoại thương Việt Nam (Vietcombank)', 'bank')}
                    className="ml-3 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    📋 Copy
                  </button>
                </div>

                {/* Branch */}
                <div className="flex justify-between items-center p-3 bg-white rounded border">
                  <div className="flex-1">
                    <div className="text-sm text-gray-600">Chi nhánh</div>
                    <div className="font-semibold">Chi nhánh Hồ Chí Minh</div>
                  </div>
                  <button
                    id="copy-branch"
                    onClick={() => copyToClipboard('Chi nhánh Hồ Chí Minh', 'branch')}
                    className="ml-3 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    📋 Copy
                  </button>
                </div>

                {/* Amount */}
                <div className="flex justify-between items-center p-3 bg-white rounded border">
                  <div className="flex-1">
                    <div className="text-sm text-gray-600">Số tiền</div>
                    <div className="font-semibold text-lg text-green-600">
                      {transactionResult?.amount ? 
                        new Intl.NumberFormat('vi-VN').format(transactionResult.amount) : 
                        (amount ? new Intl.NumberFormat('vi-VN').format(parseFloat(amount)) : '0')
                      } VND
                    </div>
                  </div>
                  <button
                    id="copy-amount"
                    onClick={() => copyToClipboard(
                      transactionResult?.amount ? 
                        transactionResult.amount.toString() : 
                        (amount || '0'), 
                      'amount'
                    )}
                    className="ml-3 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    📋 Copy
                  </button>
                </div>
              </div>
            </div>

            {/* Important Notes */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2 flex items-center">
                <span className="mr-2">⚠️</span>
                Lưu ý quan trọng
              </h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Vui lòng chuyển khoản chính xác số tiền đã ghi</li>
                <li>• Nội dung chuyển khoản ghi: <strong>Mã giao dịch</strong></li>
                <li>• Sau khi chuyển khoản, đợi admin xác nhận (thường trong 1-24h)</li>
                <li>• Không chuyển khoản vào cuối tuần hoặc ngày lễ</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Hoàn tất
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Withdraw Modal Component  
const WithdrawModal = ({ isOpen, onClose, onSuccess, wallet, user }) => {
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const token = localStorage.getItem('token')

  const isBankInfoComplete = user && user.bank_name && user.bank_account_number && user.bank_account_holder
  const availableBalance = wallet ? parseFloat(wallet.balance) - parseFloat(wallet.locked_amount || 0) : 0

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!amount || amount <= 0) {
      setError('Vui lòng nhập số tiền hợp lệ (lớn hơn 0)')
      setLoading(false)
      return
    }

    const transactionAmount = parseFloat(amount)
    if (isNaN(transactionAmount)) {
      setError('Số tiền không hợp lệ')
      setLoading(false)
      return
    }

    if (!isBankInfoComplete) {
      setError('Vui lòng cập nhật đầy đủ thông tin ngân hàng trước khi rút tiền')
      setLoading(false)
      return
    }

    if (transactionAmount > availableBalance) {
      setError(`Số tiền rút không được vượt quá số dư khả dụng (${new Intl.NumberFormat('vi-VN').format(availableBalance)} VND)`)
      setLoading(false)
      return
    }

    try {
      const res = await fetch(config.WALLET_API_URL + '/create-transaction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          amount: transactionAmount, 
          type: 'WITHDRAW', 
          description: description || '' 
        })
      })
      
      if (!res.ok) {
        let errorMessage = 'Tạo lệnh rút tiền thất bại'
        try {
          const errorData = await res.json()
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail
          } else if (errorData.message) {
            errorMessage = errorData.message
          }
        } catch (parseError) {
          errorMessage = `Lỗi HTTP ${res.status}: ${res.statusText}`
        }
        throw new Error(errorMessage)
      }
      
      const data = await res.json()
      onSuccess(data)
      setAmount('')
      setDescription('')
      onClose()
    } catch (err) {
      setError(err.message || 'Có lỗi xảy ra khi tạo lệnh rút tiền')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-red-700">💸 Tạo lệnh rút tiền</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Bank Info Warning */}
        {!isBankInfoComplete && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <div className="text-yellow-800 text-sm">
              <strong>⚠️ Chưa có thông tin ngân hàng</strong>
              <p className="mt-1">Bạn cần cập nhật thông tin ngân hàng để rút tiền.</p>
              <a href="/profile" className="inline-block mt-2 px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700">
                Cập nhật ngay
              </a>
            </div>
          </div>
        )}

        {/* Available Balance */}
        {wallet && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <div className="text-sm text-blue-800">
              <strong>Số dư khả dụng:</strong> {new Intl.NumberFormat('vi-VN').format(availableBalance)} VND
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Số tiền rút *</label>
            <input 
              type="number" 
              min="1" 
              step="any" 
              value={amount} 
              onChange={e => setAmount(e.target.value)} 
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500" 
              placeholder="Nhập số tiền cần rút"
              required
              disabled={!isBankInfoComplete}
            />
            {amount && parseFloat(amount) > availableBalance && (
              <div className="mt-1 text-sm text-red-600">
                ⚠️ Vượt quá số dư khả dụng
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mô tả (tùy chọn)</label>
            <input 
              type="text" 
              value={description} 
              onChange={e => setDescription(e.target.value)} 
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500" 
              placeholder="Ghi chú cho giao dịch"
              disabled={!isBankInfoComplete}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <div className="text-red-600 text-sm">❌ {error}</div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading || !isBankInfoComplete || (amount && parseFloat(amount) > availableBalance)}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Đang tạo...' : 'Tạo lệnh rút tiền'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function WalletTransaction() {
  const { user } = useAuth()
  const token = localStorage.getItem('token')
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [transactions, setTransactions] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [wallet, setWallet] = useState(null)
  const [walletLoading, setWalletLoading] = useState(false)
  
  // Modal states
  const [showDepositModal, setShowDepositModal] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)

  // Check if bank info is complete
  const isBankInfoComplete = user && user.bank_name && user.bank_account_number && user.bank_account_holder

  // Fetch transaction history on component mount
  useEffect(() => {
    fetchTransactionHistory()
    fetchWalletInfo()
  }, [])

  // Fetch wallet info
  const fetchWalletInfo = async () => {
    setWalletLoading(true)
    try {
      const res = await fetch(config.WALLET_API_URL, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (res.ok) {
        const data = await res.json()
        setWallet(data)
      } else {
        console.error('Error fetching wallet info: HTTP', res.status, res.statusText)
      }
    } catch (err) {
      console.error('Error fetching wallet info:', err)
      setError('Không thể tải thông tin ví: ' + (err.message || err.toString()))
    } finally {
      setWalletLoading(false)
    }
  }

  // Refresh history after creating new transaction
  useEffect(() => {
    if (result) {
      setTimeout(() => {
        fetchTransactionHistory()
        fetchWalletInfo() // Refresh wallet info too
      }, 1000)
    }
  }, [result])

  const fetchTransactionHistory = async () => {
    setLoadingHistory(true)
    try {
      const res = await fetch(config.WALLET_API_URL + '/history', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (res.ok) {
        const data = await res.json()
        console.log('Transaction data received:', data) // Debug log
        setTransactions(Array.isArray(data) ? data : [])
      } else {
        console.error('Error fetching transaction history: HTTP', res.status, res.statusText)
        setError('Không thể tải lịch sử giao dịch')
      }
    } catch (err) {
      console.error('Error fetching transaction history:', err)
      setError('Lỗi khi tải lịch sử giao dịch: ' + (err.message || err.toString()))
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleTransactionSuccess = (data) => {
    setResult(data)
    setError('')
    // Refresh data after successful transaction
    setTimeout(() => {
      fetchTransactionHistory()
      fetchWalletInfo()
    }, 1000)
  }

  const getStatusText = (transaction) => {
    console.log('Transaction status check:', {
      id: transaction.id,
      approved_by: transaction.approved_by,
      rejected_by: transaction.rejected_by,
      reject_reason: transaction.reject_reason
    }) // Debug log
    
    if (transaction.approved_by) {
      return <span className="text-green-600 font-semibold">Đã duyệt</span>
    } else if (transaction.rejected_by) {
      return (
        <span className="text-red-600 font-semibold">
          Đã từ chối
          {transaction.reject_reason && (
            <div className="text-sm font-normal">Lý do: {transaction.reject_reason}</div>
          )}
        </span>
      )
    } else {
      return <span className="text-yellow-600 font-semibold">Chờ duyệt</span>
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN')
  }

  const formatAmount = (amount, type) => {
    const formattedAmount = new Intl.NumberFormat('vi-VN').format(amount)
    if (type === 'WITHDRAW') {
      return <span className="text-red-600">-{formattedAmount} VND</span>
    } else {
      return <span className="text-green-600">+{formattedAmount} VND</span>
    }
  }

  return (
    <div className="max-w-4xl mx-auto mt-8 p-6 space-y-8">
      {/* Header & Action Buttons */}
      <div className="bg-white rounded shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Quản lý ví điện tử</h2>
          <div className="flex gap-3">
            <button
              onClick={() => setShowDepositModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center gap-2"
            >
              <span>💰</span>
              Tạo lệnh nạp tiền
            </button>
            <button
              onClick={() => setShowWithdrawModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold flex items-center gap-2"
            >
              <span>💸</span>
              Tạo lệnh rút tiền
            </button>
          </div>
        </div>
        
        {/* Wallet Balance Info */}
        {wallet && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded">
            <h3 className="font-semibold text-blue-800 mb-2">Thông tin ví</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Số dư:</span>
                <div className="font-semibold text-green-600">
                  {new Intl.NumberFormat('vi-VN').format(wallet.balance)} VND
                </div>
              </div>
              <div>
                <span className="text-gray-600">Bị khóa:</span>
                <div className="font-semibold text-red-600">
                  {new Intl.NumberFormat('vi-VN').format(wallet.locked_amount || 0)} VND
                </div>
              </div>
              <div>
                <span className="text-gray-600">Có thể rút:</span>
                <div className="font-semibold text-blue-600">
                  {new Intl.NumberFormat('vi-VN').format(parseFloat(wallet.balance) - parseFloat(wallet.locked_amount || 0))} VND
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Bank Info Warning for Withdraw */}
        {!isBankInfoComplete && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
            <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Thông tin tài khoản ngân hàng chưa đầy đủ</h3>
            <p className="text-sm text-yellow-700 mb-2">
              Để thực hiện giao dịch rút tiền, bạn cần cập nhật đầy đủ thông tin tài khoản ngân hàng trong hồ sơ.
            </p>
            <a 
              href="/profile" 
              className="inline-block px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
            >
              Cập nhật thông tin ngân hàng
            </a>
          </div>
        )}

        {/* Success/Error Messages */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
            <div className="text-red-600 font-semibold">❌ Lỗi:</div>
            <div className="text-red-600">{String(error)}</div>
          </div>
        )}
        {result && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
            <div className="font-bold text-green-700">✅ Giao dịch đã gửi, chờ admin duyệt!</div>
            <div className="text-sm text-gray-600 mt-1">
              Mã giao dịch: <span className="font-mono font-semibold">{result.transaction_code || result.transaction_id || result.id}</span>
            </div>
            {result.amount && (
              <div className="text-sm text-gray-600">
                Số tiền: <span className="font-semibold">{new Intl.NumberFormat('vi-VN').format(result.amount)} VND</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Lịch sử giao dịch</h3>
          <button 
            onClick={fetchTransactionHistory} 
            disabled={loadingHistory}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loadingHistory ? 'Đang tải...' : 'Làm mới'}
          </button>
        </div>
        
        {loadingHistory ? (
          <div className="text-center py-4">Đang tải lịch sử giao dịch...</div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-4 text-gray-500">Chưa có giao dịch nào</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-4 py-2 text-left">Mã GD</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Loại</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Số tiền</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Mô tả</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Thời gian</th>
                  <th className="border border-gray-200 px-4 py-2 text-left">Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx.id} className="hover:bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2">{tx.transaction_code || `#${tx.id}`}</td>
                    <td className="border border-gray-200 px-4 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        tx.type === 'DEPOSIT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {tx.type === 'DEPOSIT' ? 'Nạp tiền' : 'Rút tiền'}
                      </span>
                    </td>
                    <td className="border border-gray-200 px-4 py-2 font-mono">
                      {formatAmount(tx.amount, tx.type)}
                    </td>
                    <td className="border border-gray-200 px-4 py-2">{tx.description || '-'}</td>
                    <td className="border border-gray-200 px-4 py-2 text-sm">
                      {formatDate(tx.created_at)}
                    </td>
                    <td className="border border-gray-200 px-4 py-2">
                      {getStatusText(tx)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Modals */}
      <DepositModal 
        isOpen={showDepositModal} 
        onClose={() => setShowDepositModal(false)} 
        onSuccess={handleTransactionSuccess}
        wallet={wallet}
      />
      
      <WithdrawModal 
        isOpen={showWithdrawModal} 
        onClose={() => setShowWithdrawModal(false)} 
        onSuccess={handleTransactionSuccess}
        wallet={wallet}
        user={user}
      />
    </div>
  )
}

export default WalletTransaction
